import type { BadgeVariant } from '@/components/ui'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  ClaimPayout,
  ClaimPayrollMonth,
  ClaimStatus,
  ClaimStepRole,
  ClaimStepView,
} from '../types/expenseClaim.types'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** { year: 2026, month: 11 } -> "November 2026". */
export function formatPayrollMonth(month: ClaimPayrollMonth): string {
  const name = MONTHS[month.month - 1]
  return name ? `${name} ${month.year}` : `${month.year}-${String(month.month).padStart(2, '0')}`
}

/** The one line a claimant reads to know when they get the money back. */
export function payoutLabel(payout: ClaimPayout): string {
  switch (payout.state) {
    case 'awaiting_approval':
      return 'Awaiting approval'
    case 'scheduled':
      return payout.month
        ? `To be paid in ${formatPayrollMonth(payout.month)}`
        : 'To be paid in the next payroll'
    case 'paid':
      return payout.month ? `Paid in ${formatPayrollMonth(payout.month)}` : 'Paid in payroll'
    case 'paid_outside_payroll':
      return 'Paid outside payroll'
    case 'closed':
    default:
      return 'Not payable'
  }
}

export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  reimbursed: 'Reimbursed',
}

export const CLAIM_STATUS_VARIANT: Record<ClaimStatus, BadgeVariant> = {
  submitted: 'warning',
  approved: 'info',
  rejected: 'danger',
  withdrawn: 'neutral',
  reimbursed: 'success',
}

export const CLAIM_STEP_LABEL: Record<ClaimStepRole, string> = {
  manager: 'Manager',
  admin: 'Admin',
}

/** "Manager: approved", "Admin: waiting", "Manager: skipped". */
export function stepSummary(step: ClaimStepView): string {
  const who = CLAIM_STEP_LABEL[step.role] ?? step.role
  switch (step.status) {
    case 'approved':
      return `${who}: approved${step.decidedBy ? ` by ${step.decidedBy.name}` : ''}`
    case 'rejected':
      return `${who}: rejected${step.decidedBy ? ` by ${step.decidedBy.name}` : ''}`
    case 'skipped':
      return `${who}: skipped`
    case 'pending':
    default:
      return step.isCurrent ? `${who}: waiting` : `${who}: not started`
  }
}

const CLAIM_ERROR_COPY: Record<string, string> = {
  EXPENSE_CLAIM_RECEIPT_INVALID:
    'One of the receipts could not be attached. Upload the receipt again from this form, then submit.',
  EXPENSE_CLAIM_STATE:
    'This claim has already moved on (it may have been decided or paid). Refresh to see its current status.',
  EXPENSE_CLAIM_NOT_PAYABLE:
    'This claim cannot be paid through payroll: the employee has no active pay record in this company. An admin can pay it outside payroll and mark it reimbursed.',
  EXPENSE_CLAIM_SELF_APPROVAL: 'You cannot approve or reimburse your own expense claim.',
  EXPENSE_CLAIM_NOT_APPROVER: 'You are not an approver for the current step of this claim.',
}

/**
 * Copy for the expense-claim error codes; any other error falls back to the
 * app-wide message mapping.
 */
export function getClaimErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | Record<string, unknown>
    | undefined
  const code = typeof data?.code === 'string' ? data.code : undefined
  if (code === 'EXPENSE_CLAIM_CURRENCY_MISMATCH') {
    const expected = typeof data?.expectedCurrency === 'string' ? data.expectedCurrency : undefined
    return expected
      ? `Claims are paid back with your salary, so the amount must be in ${expected}, your pay currency for this company.`
      : 'Claims are paid back with your salary, so the amount must be in your pay currency for this company.'
  }
  if (code && CLAIM_ERROR_COPY[code]) {
    // Not-payable carries the specific reason from the server; prefer it when present.
    if (code === 'EXPENSE_CLAIM_NOT_PAYABLE' && typeof data?.message === 'string' && data.message) {
      return data.message
    }
    return CLAIM_ERROR_COPY[code]
  }
  return getErrorMessage(error)
}
